import { DcfComponentBase } from './dcf-base.js';
import { InlineEditor } from './inline-editor.js';

class ReverseDcfModel extends DcfComponentBase {

	widgetWillMount() {
		// ══════════════════════════════════════════════
		//  Shared computed fields (from base class)
		// ══════════════════════════════════════════════

		this._defineWaccComputed();

		// ── RDCF-specific: per-period ratios driven by fixed assumptions ──
		// Must be defined BEFORE _defineProjectionComputed() so that
		// absolute values (D&A, Capex, ΔWC, SBC) can read the ratios.
		for (let i = 0; i < this.state.periods.length; i++) {
			if (this.state.periods[i].isActual) continue;

			this.defineComputed(
				st => st.periods[i], 'daRatio',
				() => this.state.fixedDaRatio || 0.05
			);
			this.defineComputed(
				st => st.periods[i], 'capexRatio',
				() => this.state.fixedCapexRatio || 0.08
			);
			this.defineComputed(
				st => st.periods[i], 'nwcRatio',
				() => this.state.fixedNwcRatio || 0.01
			);
			this.defineComputed(
				st => st.periods[i], 'sbcRatio',
				() => this.state.fixedSbcRatio || 0
			);
		}

		this._defineProjectionComputed();
		this._defineSumPvFcf();
		this._defineTvComputed();
		this._defineProjectionSubtitle();
		this._defineSensitivityComputed();

		// ══════════════════════════════════════════════
		//  RDCF-specific: Reverse Solve Chain
		// ══════════════════════════════════════════════

		// Equity Value = CurrentSharePrice × DilutedShares, converted to ReportCurrency
		this.defineComputed('equityValue', () => {
			const s = this.state;
			const evPriceCcy = s.currentSharePrice * s.dilutedSharesOutstanding;
			return s.fxRate !== 0 ? evPriceCcy / s.fxRate : evPriceCcy;
		});

		// Required EV
		this.defineComputed('requiredEnterpriseValue', () => {
			const s = this.state;
			return s.equityValue
				+ s.totalDebt
				+ s.minorityInterest
				+ s.preferredEquity
				- s.cash
				- s.equityInvestments
				- s.nonOperatingAssets;
		});

		// Cumulative PV of FCFs
		this.defineComputed('cumulativePvFcf', () => {
			const result = [];
			let cumSum = 0;
			for (const p of this.state.periods) {
				if (p.isActual) continue;
				cumSum += (p.pvFcf || 0);
				result.push(cumSum);
			}
			return result;
		});

		// Required PV of Terminal Value
		this.defineComputed('requiredPvTerminalValue', () => {
			return this.state.requiredEnterpriseValue - this.state.sumPvFcf;
		});

		this.defineComputed('impliedTvUndiscounted', () => {
			const tvDf = this.state.tvDiscountFactor;
			if (tvDf === 0) return 0;
			return this.state.requiredPvTerminalValue / tvDf;
		});

		// ══════════════════════════════════════════════
		//  RDCF-specific: Solver
		// ══════════════════════════════════════════════

		this.defineComputed('impliedSolveValue', () => {
			const s = this.state;
			switch (s.solveVariable) {
				case 'revenue_growth':
					return this._solveForRevenueGrowth();
				case 'ebitda_margin':
					return this._solveForEbitdaMargin();
				case 'wacc':
					return this._solveForWacc();
				case 'terminal_growth_rate':
					return this._solveForTerminalGrowthRate();
				default:
					return this._solveForRevenueGrowth();
			}
		});

		// ══════════════════════════════════════════════
		//  RDCF-specific: Headline & Labels
		// ══════════════════════════════════════════════

		this.defineComputed('impliedSolveLabel', () => {
			switch (this.state.solveVariable) {
				case 'revenue_growth': return 'Implied Revenue CAGR';
				case 'ebitda_margin': return 'Implied Steady-State EBITDA Margin';
				case 'wacc': return 'Implied Discount Rate';
				case 'terminal_growth_rate': return 'Implied Terminal Growth Rate';
				default: return 'Implied Value';
			}
		});

		this.defineComputed('impliedSolveResultLabel', () => {
			return '→ ' + this.state.impliedSolveLabel;
		});

		this.defineComputed('plausibilityBadge', () => {
			const s = this.state;
			if (s.impliedSolveValue == null) return '⚠ N/A';
			switch (s.plausibility) {
				case 'reasonable': return '✓ Reasonable';
				case 'aggressive': return '⚡ Aggressive';
				case 'implausible': return '✗ Implausible';
				default: return '';
			}
		});

		this.defineComputed('metaSummary', () => {
			const s = this.state;
			const solveLabels = {
				'revenue_growth': 'Revenue Growth',
				'ebitda_margin': 'EBITDA Margin',
				'wacc': 'WACC',
				'terminal_growth_rate': 'Terminal Growth Rate'
			};
			const parts = [
				`Valuation Date: ${this._formatDate(s.valuationDate)}`,
				s.dcfApproach.toUpperCase(),
				`${s.projectionYears}-Year Projection`,
				s.midYearConvention ? 'Mid-Year Convention' : 'Year-End Convention',
				`Solve For: ${solveLabels[s.solveVariable] || s.solveVariable}`
			];
			if (this._isCrossCurrency()) {
				parts.push(`${s.reportCurrency} (report) → ${s.priceCurrency} (price) @ ${s.fxRate.toFixed(4)}`);
			} else {
				parts.push(`${s.reportCurrency}, ${s.units}`);
			}
			return parts.join(' · ');
		});

		// ══════════════════════════════════════════════
		//  RDCF-specific: Gap Analysis & Sensitivity labels
		// ══════════════════════════════════════════════

		this.defineComputed('interpretationText', () => {
			const s = this.state;
			if (s.impliedSolveValue == null) return '';

			const implied = (s.impliedSolveValue * 100).toFixed(1);
			const label = this.state.impliedSolveLabel.toLowerCase();
			const hist5y = s.gapBenchmarks?.find(b => b.label.includes('5Y'));

			if (hist5y) {
				const histVal = (hist5y.value * 100).toFixed(1);
				const comparison = s.impliedSolveValue > hist5y.value ? 'above' : 'below';
				return `The market is pricing in ${implied}% ${label} over ${s.projectionYears} years. This is ${comparison} the company's 5-year historical value of ${histVal}%.`;
			}
			return `The market is pricing in ${implied}% ${label} over ${s.projectionYears} years.`;
		});

		this.defineComputed('sensitivityOutputLabel', () => {
			return this.state.impliedSolveLabel;
		});

		this.defineComputed('sensitivityContextText', () => {
			const hist5y = this.state.gapBenchmarks?.find(b => b.label.includes('5Y'));
			if (hist5y) {
				return `Historical 5Y value: ${(hist5y.value * 100).toFixed(1)}%`;
			}
			return '';
		});
	}

	// ══════════════════════════════════════════════
	//  Override: WACC computation for solve mode
	// ══════════════════════════════════════════════

	_computeWacc() {
		const s = this.state;
		if (s.solveVariable === 'wacc') {
			return s.impliedSolveValue != null ? s.impliedSolveValue : 0.10;
		}
		return super._computeWacc();
	}

	// ══════════════════════════════════════════════
	//  Lifecycle hooks
	// ══════════════════════════════════════════════

	widgetDidMount() {
		this._updateVisibility();
		this._updateGapBars();
		this._updateSensitivityCells();
		this._updateHideIfZeroRows();
		this._updatePlausibilityBadgeClass();
		this._updateSolveStepVisibility();

		InlineEditor.attach(this.root, this.state, this.fields);
	}

	widgetContentChanged() {
		this._updateVisibility();
		this._updateGapBars();
		this._updateSensitivityCells();
		this._updateHideIfZeroRows();
		this._updatePlausibilityBadgeClass();
		this._updateSolveStepVisibility();
	}

	// ══════════════════════════════════════════════
	//  Solver implementations
	// ══════════════════════════════════════════════

	_getFixedOpts() {
		const s = this.state;
		return {
			daRatio: s.fixedDaRatio || 0.05,
			capexRatio: s.fixedCapexRatio || 0.08,
			nwcRatio: s.fixedNwcRatio || 0.01,
			sbcRatio: s.fixedSbcRatio || 0,
			taxRate: s.fixedTaxRate || 0.21
		};
	}

	_solveForRevenueGrowth() {
		const s = this.state;
		const requiredEv = s.requiredEnterpriseValue;
		if (requiredEv <= 0) return null;
		const baseRevenue = this._baseRevenue();
		if (baseRevenue <= 0) return null;

		const opts = this._getFixedOpts();
		const margin = s.fixedEbitdaMargin || 0.30;

		return this._bisect(-0.50, 1.00, g => {
			const proj = this._buildProjections(baseRevenue, g, margin, s.wacc, opts);
			const ev = proj.sumPv + this._calcPvTv(s.wacc, proj.lastUfcf, proj.lastEbitda, s.terminalGrowthRate, s.projectionYears);
			return ev - requiredEv;
		});
	}

	_solveForEbitdaMargin() {
		const s = this.state;
		const requiredEv = s.requiredEnterpriseValue;
		if (requiredEv <= 0) return null;
		const baseRevenue = this._baseRevenue();
		if (baseRevenue <= 0) return null;

		const opts = this._getFixedOpts();
		const growth = s.fixedRevenueGrowth || 0.08;

		return this._bisect(-0.50, 1.00, m => {
			const proj = this._buildProjections(baseRevenue, growth, m, s.wacc, opts);
			const ev = proj.sumPv + this._calcPvTv(s.wacc, proj.lastUfcf, proj.lastEbitda, s.terminalGrowthRate, s.projectionYears);
			return ev - requiredEv;
		});
	}

	_solveForWacc() {
		const s = this.state;
		const requiredEv = s.requiredEnterpriseValue;
		if (requiredEv <= 0) return null;
		const baseRevenue = this._baseRevenue();
		if (baseRevenue <= 0) return null;

		const opts = this._getFixedOpts();
		const growth = s.fixedRevenueGrowth || 0.08;
		const margin = s.fixedEbitdaMargin || 0.30;

		return this._bisect(0.01, 0.30, r => {
			const proj = this._buildProjections(baseRevenue, growth, margin, r, opts);
			const ev = proj.sumPv + this._calcPvTv(r, proj.lastUfcf, proj.lastEbitda, s.terminalGrowthRate, s.projectionYears);
			return ev - requiredEv;
		});
	}

	_solveForTerminalGrowthRate() {
		const s = this.state;
		const requiredEv = s.requiredEnterpriseValue;
		if (requiredEv <= 0) return null;
		const baseRevenue = this._baseRevenue();
		if (baseRevenue <= 0) return null;

		const opts = this._getFixedOpts();
		const growth = s.fixedRevenueGrowth || 0.08;
		const margin = s.fixedEbitdaMargin || 0.30;

		return this._bisect(-0.02, (s.wacc - 0.001), gt => {
			const proj = this._buildProjections(baseRevenue, growth, margin, s.wacc, opts);
			const ev = proj.sumPv + this._calcPvTv(s.wacc, proj.lastUfcf, proj.lastEbitda, gt, s.projectionYears);
			return ev - requiredEv;
		});
	}

	// ══════════════════════════════════════════════
	//  Sensitivity: implied solve value per cell
	//  FIX: No state mutation — passes params through arguments
	// ══════════════════════════════════════════════

	_calcSensitivityCell(table, rowVal, colVal) {
		const s = this.state;
		const baseRevenue = this._baseRevenue();
		if (baseRevenue <= 0) return 0;

		const wacc = rowVal;
		const tgr = table.colFormat === 'pct' ? colVal : s.terminalGrowthRate;
		const exitMult = table.colFormat === 'multiple' ? colVal : s.exitMultiple;
		const requiredEv = s.requiredEnterpriseValue;

		const opts = this._getFixedOpts();
		const margin = s.fixedEbitdaMargin || 0.30;
		const growth = s.fixedRevenueGrowth || 0.08;

		// Solve using overridden params (no state mutation)
		const solveVar = s.solveVariable;
		let result = null;

		switch (solveVar) {
			case 'revenue_growth':
				result = this._bisect(-0.50, 1.00, g => {
					const proj = this._buildProjectionsWithOverrides(baseRevenue, g, margin, wacc, opts);
					const ev = proj.sumPv + this._calcPvTvWithOverrides(wacc, proj.lastUfcf, proj.lastEbitda, tgr, s.projectionYears, exitMult);
					return ev - requiredEv;
				});
				break;
			case 'ebitda_margin':
				result = this._bisect(-0.50, 1.00, m => {
					const proj = this._buildProjectionsWithOverrides(baseRevenue, growth, m, wacc, opts);
					const ev = proj.sumPv + this._calcPvTvWithOverrides(wacc, proj.lastUfcf, proj.lastEbitda, tgr, s.projectionYears, exitMult);
					return ev - requiredEv;
				});
				break;
			default:
				result = this._bisect(-0.50, 1.00, g => {
					const proj = this._buildProjectionsWithOverrides(baseRevenue, g, margin, wacc, opts);
					const ev = proj.sumPv + this._calcPvTvWithOverrides(wacc, proj.lastUfcf, proj.lastEbitda, tgr, s.projectionYears, exitMult);
					return ev - requiredEv;
				});
		}

		return result || 0;
	}

	/**
	 * Build projections with explicit wacc param (doesn't read from state.wacc).
	 * Used by sensitivity to avoid state mutation.
	 */
	_buildProjectionsWithOverrides(baseRevenue, growth, margin, wacc, opts) {
		const daRatio = opts.daRatio || 0.05;
		const capexRatio = opts.capexRatio || 0.08;
		const nwcRatio = opts.nwcRatio || 0.01;
		const sbcRatio = opts.sbcRatio || 0;
		const taxRate = opts.taxRate || 0.21;
		const projYears = this.state.projectionYears;

		let prevRev = baseRevenue, sumPv = 0, lastUfcf = 0, lastEbitda = 0;

		for (let i = 1; i <= projYears; i++) {
			const rev = prevRev * (1 + growth);
			const ebitda = rev * margin;
			const da = rev * daRatio;
			const ebit = ebitda - da;
			const nopat = ebit * (1 - taxRate);
			const ufcf = nopat + da - rev * capexRatio - rev * nwcRatio + rev * sbcRatio;

			const period = this.state.midYearConvention ? i - 0.5 : i;
			sumPv += ufcf * Math.pow(1 + wacc, -period);

			lastUfcf = ufcf;
			lastEbitda = ebitda;
			prevRev = rev;
		}

		return { sumPv, lastUfcf, lastEbitda };
	}

	/**
	 * Calculate PV of Terminal Value with explicit overrides (no state.exitMultiple dependency).
	 */
	_calcPvTvWithOverrides(wacc, lastUfcf, lastEbitda, tgr, projYears, exitMult) {
		const s = this.state;
		const denom = wacc - tgr;
		const tvPerp = denom > 0 ? (lastUfcf * (1 + tgr)) / denom : 0;
		const tvExit = lastEbitda * exitMult;

		let tvUndiscounted;
		switch (s.terminalValueMethod) {
			case 'perpetuity_growth': tvUndiscounted = tvPerp; break;
			case 'exit_multiple': tvUndiscounted = tvExit; break;
			case 'blended':
			default:
				tvUndiscounted = tvPerp * s.blendWeightPerpetuity + tvExit * s.blendWeightExit;
		}

		return tvUndiscounted * Math.pow(1 + wacc, -projYears);
	}

	// ══════════════════════════════════════════════
	//  RDCF-specific UI Update Methods
	// ══════════════════════════════════════════════

	_updateVisibility() {
		const root = document.getElementById(this.root);
		if (!root) return;
		const s = this.state;

		// Assumptions panel: show/hide implied result vs editable inputs
		const solveWacc = s.solveVariable === 'wacc';
		root.querySelectorAll('.rdcf-implied-wacc-result').forEach(el => { el.style.display = solveWacc ? '' : 'none'; });
		root.querySelectorAll('.rdcf-wacc-inputs').forEach(el => { el.style.display = solveWacc ? 'none' : ''; });

		const solveTgr = s.solveVariable === 'terminal_growth_rate';
		root.querySelectorAll('.rdcf-implied-tgr-result').forEach(el => { el.style.display = solveTgr ? '' : 'none'; });
		root.querySelectorAll('.rdcf-tgr-editable').forEach(el => { el.style.display = solveTgr ? 'none' : ''; });

		const solveMargin = s.solveVariable === 'ebitda_margin';
		root.querySelectorAll('.rdcf-implied-margin-result').forEach(el => { el.style.display = solveMargin ? '' : 'none'; });
		root.querySelectorAll('.rdcf-margin-editable').forEach(el => { el.style.display = solveMargin ? 'none' : ''; });

		const solveGrowth = s.solveVariable === 'revenue_growth';
		root.querySelectorAll('.rdcf-implied-growth-result').forEach(el => { el.style.display = solveGrowth ? '' : 'none'; });
		root.querySelectorAll('.rdcf-growth-inputs').forEach(el => { el.style.display = solveGrowth ? 'none' : ''; });

		// Projection table: toggle implied rows
		root.querySelectorAll('.row-implied-candidate').forEach(row => {
			const impliedWhen = row.dataset.impliedWhen;
			const isImplied = impliedWhen === s.solveVariable;
			row.classList.toggle('row-implied', isImplied);

			const tag = row.querySelector('.implied-tag');
			if (tag) tag.style.display = isImplied ? '' : 'none';

			row.querySelectorAll('td').forEach(td => {
				if (td.classList.contains('col-actual')) return;
				td.classList.toggle('cell-implied', isImplied);
				td.classList.toggle('editable', !isImplied);
			});
		});

		this._updateBlendWeightVisibility();

		// FCFF vs FCFE
		const isFcfe = s.dcfApproach === 'fcfe';
		root.querySelectorAll('.rdcf-cost-of-debt').forEach(el => { el.style.display = isFcfe ? 'none' : ''; });

		this._updateCrossCurrencyVisibility();

		// Solve warning
		root.querySelectorAll('.rdcf-solve-warning').forEach(el => { el.style.display = s.solveWarning ? '' : 'none'; });
	}

	_updateGapBars() {
		const root = document.getElementById(this.root);
		if (!root) return;
		const s = this.state;

		let maxVal = Math.abs(s.impliedSolveValue || 0);
		if (s.gapBenchmarks) {
			for (const b of s.gapBenchmarks) {
				maxVal = Math.max(maxVal, Math.abs(b.value || 0));
			}
		}
		if (maxVal === 0) maxVal = 1;

		const impliedBar = root.querySelector('.gap-fill-implied');
		if (impliedBar && s.impliedSolveValue != null) {
			const pct = Math.min(Math.abs(s.impliedSolveValue) / maxVal * 80, 100);
			impliedBar.style.width = pct + '%';
		}

		if (s.gapBenchmarks) {
			s.gapBenchmarks.forEach((b, idx) => {
				const bar = root.querySelector(`[data-gap-benchmark-index="${idx}"]`);
				if (bar) {
					const pct = Math.min(Math.abs(b.value) / maxVal * 80, 100);
					bar.style.width = pct + '%';
				}
			});
		}
	}

	_updateSensitivityCells() {
		const root = document.getElementById(this.root);
		if (!root) return;

		this.state.sensitivityTables.forEach((st, tIdx) => {
			const tableEl = root.querySelector(`.rdcf-sensitivity-heatmap[data-table-index="${tIdx}"]`);
			if (!tableEl) return;

			tableEl.querySelectorAll('th[data-col]').forEach(th => {
				const c = parseInt(th.dataset.col);
				if (c < st.colValues.length) {
					th.textContent = this._formatAxisValue(st.colValues[c], st.colFormat);
					th.className = c === st.baseColIndex ? 'r base-axis' : 'r';
					th.style.display = '';
				} else {
					th.style.display = 'none';
				}
			});

			tableEl.querySelectorAll('tr[data-row]').forEach(tr => {
				const r = parseInt(tr.dataset.row);
				if (r >= st.rowValues.length) { tr.style.display = 'none'; return; }
				tr.style.display = '';
				tr.className = r === st.baseRowIndex ? 'base-row' : '';

				const rowHeader = tr.querySelector('[data-row-header]');
				if (rowHeader) {
					rowHeader.textContent = this._formatAxisValue(st.rowValues[r], st.rowFormat);
					rowHeader.className = r === st.baseRowIndex ? 'axis-label base-axis' : 'axis-label';
				}

				tr.querySelectorAll('td[data-col]').forEach(td => {
					const c = parseInt(td.dataset.col);
					if (c >= st.colValues.length) { td.style.display = 'none'; return; }
					td.style.display = '';

					const val = st.matrix && st.matrix[r] && st.matrix[r][c] != null ? st.matrix[r][c] : 0;
					td.textContent = (val * 100).toFixed(1) + '%';

					const isBase = r === st.baseRowIndex && c === st.baseColIndex;
					const heatClass = this._getImpliedHeatmapClass(val);
					td.className = `r ${heatClass}${isBase ? ' base-cell' : ''}`;
				});
			});
		});
	}

	_updatePlausibilityBadgeClass() {
		const root = document.getElementById(this.root);
		if (!root) return;
		const badge = root.querySelector('.plausibility-badge');
		if (!badge) return;

		badge.classList.remove('plausibility-reasonable', 'plausibility-aggressive', 'plausibility-implausible');
		switch (this.state.plausibility) {
			case 'reasonable': badge.classList.add('plausibility-reasonable'); break;
			case 'aggressive': badge.classList.add('plausibility-aggressive'); break;
			case 'implausible': badge.classList.add('plausibility-implausible'); break;
		}
	}

	_updateSolveStepVisibility() {
		const root = document.getElementById(this.root);
		if (!root) return;

		root.querySelectorAll('.solve-step-hide-zero').forEach(step => {
			const varName = step.dataset.solveVar;
			if (varName) {
				const val = this.state[varName];
				step.style.display = (val === 0 || val == null) ? 'none' : '';
			}
		});
	}

	_getImpliedHeatmapClass(value) {
		const sv = this.state.solveVariable;
		if (sv === 'revenue_growth' || sv === 'ebitda_margin') {
			if (value > 0.25) return 'heat-strong-down';
			if (value > 0.15) return 'heat-down';
			if (value >= 0 && value <= 0.15) return 'heat-neutral';
			if (value >= -0.05) return 'heat-up';
			return 'heat-strong-up';
		}
		if (value > 0.15) return 'heat-strong-down';
		if (value > 0.10) return 'heat-down';
		if (value >= 0.02) return 'heat-neutral';
		return 'heat-up';
	}
}

Tango.registerComponent(ReverseDcfModel);